// Game sounds. Weapons and impacts play recordings when there are some (the player's CS:S files,
// else Kenney's CC0 sounds, see samples.ts); everything else, and anything without a recording, is
// synthesised with WebAudio at runtime.

import type { Surface } from '../sim/simulation';
import { SampleBank, type SampleId } from './samples';

/** Where a sound comes from, relative to the listener. */
export interface SoundPos {
  /** Distance in Source units. */
  dist: number;
  /** -1 left .. 1 right. */
  pan: number;
}

export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Where synthesised sounds go (master, or a bus that can be cut off). */
  private dest: AudioNode | null = null;
  private noise: AudioBuffer | null = null;
  private reverb: ConvolverNode | null = null;
  private volume = 0.5;
  private stepSide = 0;
  /** Reload/silencer sounds still to come; cut off when the weapon changes. */
  private weaponBus: GainNode | null = null;
  private readonly bank = new SampleBank();
  private gameFiles: Map<string, Uint8Array> | null = null;
  /** While pre-rendering: the offline context the synth helpers draw into. */
  private synthCtx: BaseAudioContext | null = null;
  /**
   * Footsteps (a few variants per foot) and the landing thump, rendered once: playing a ready
   * buffer is two audio nodes instead of about ten per jump, which kept piling up while hopping.
   */
  private readonly steps: AudioBuffer[][] = [[], []];
  private thump: AudioBuffer | null = null;
  /** Played when a run finishes (public/assets/sounds/win_sound.mp3). */
  private winBuffer: AudioBuffer | null = null;

  /** Must be called from a user gesture (browsers keep audio off until then). */
  unlock() {
    if (!this.ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
      this.dest = this.master;
      const n = this.ctx.sampleRate;
      this.noise = this.ctx.createBuffer(1, n, n);
      const d = this.noise.getChannelData(0);
      let seed = 1;
      for (let i = 0; i < n; i++) d[i] = ((seed = (seed * 16807) % 2147483647) / 2147483647) * 2 - 1;
      // A short room tail for the synthesised gunshot.
      const len = Math.floor(n * 0.6);
      const ir = this.ctx.createBuffer(2, len, n);
      for (let c = 0; c < 2; c++) {
        const ch = ir.getChannelData(c);
        for (let i = 0; i < len; i++) {
          seed = (seed * 16807) % 2147483647;
          ch[i] = ((seed / 2147483647) * 2 - 1) * Math.pow(1 - i / len, 3);
        }
      }
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = ir;
      const wet = this.ctx.createGain();
      wet.gain.value = 0.35;
      this.reverb.connect(wet).connect(this.master);
      this.bank.loadKenney(this.ctx);
      if (this.gameFiles) void this.bank.setGameFiles(this.ctx, this.gameFiles);
      void this.prerender().catch(() => {});
      void this.loadWin(this.ctx);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
  }

  /** CS:S sound files (game path -> bytes) from the player's game or skin packs. */
  setGameSounds(files: Map<string, Uint8Array>) {
    this.gameFiles = files;
    if (this.ctx) void this.bank.setGameFiles(this.ctx, files);
  }

  /** The audio context once unlocked (null before the first click/key). */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Master output (volume setting applied). */
  get output(): AudioNode | null {
    return this.master;
  }

  /** 0..1 */
  setVolume(v: number) {
    this.volume = v;
    if (this.master) this.master.gain.value = v;
  }

  private get ready() {
    return !!(this.ctx && this.master && this.noise && this.volume > 0 && this.ctx.state === 'running');
  }

  /**
   * Play a recording. Returns false when there is none (the caller synthesises one instead).
   * Game recordings are mixed a little quieter than Kenney's, which are normalised lower.
   */
  private play(id: SampleId, gain: number, at = 0, pos?: SoundPos): boolean {
    const s = this.bank.pick(id);
    if (!s) return false;
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = s.buffer;
    src.playbackRate.value = 0.96 + Math.random() * 0.08;
    let node: AudioNode = src;
    const g = ctx.createGain();
    g.gain.value = gain * (s.game ? 0.7 : 1) * (pos ? falloff(pos.dist) : 1);
    node = node.connect(g);
    if (pos && ctx.createStereoPanner) {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pos.pan)) * 0.8;
      node = node.connect(p);
    }
    node.connect(this.dest!);
    src.start(ctx.currentTime + at);
    return true;
  }

  /** Render the movement sounds into buffers (see `steps`). */
  private async prerender() {
    const rate = this.ctx!.sampleRate;
    const render = (fn: () => void, seconds: number) => {
      const off = new OfflineAudioContext(1, Math.ceil(rate * seconds), rate);
      const dest = this.dest;
      this.synthCtx = off;
      this.dest = off.destination;
      try {
        fn();
      } finally {
        this.synthCtx = null;
        this.dest = dest;
      }
      return off.startRendering();
    };
    for (let side = 0; side < 2; side++)
      for (let v = 0; v < 4; v++)
        this.steps[side].push(await render(() => this.stepSynth(side ? 1 : 0.88, 1), 0.2));
    this.thump = await render(() => this.tone(70, 0.35, 0.12, 'sine', 0, 40), 0.25);
  }

  private async loadWin(ctx: AudioContext) {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL ?? '/'}assets/sounds/win_sound.mp3`);
      if (res.ok) this.winBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      // No win sound; finishing is silent.
    }
  }

  /** A run finished. */
  win() {
    if (!this.ready || !this.winBuffer) return;
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.winBuffer;
    const g = ctx.createGain();
    g.gain.value = 0.8;
    src.connect(g).connect(this.master!);
    src.start();
  }

  /** Play a ready buffer at a volume. */
  private playBuffer(buf: AudioBuffer, gain: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 0.97 + Math.random() * 0.06;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.dest!);
    src.start();
  }

  private get synth(): BaseAudioContext {
    return this.synthCtx ?? this.ctx!;
  }

  private env(gain: number, attack: number, decay: number, at: number): GainNode {
    const g = this.synth.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    g.connect(this.dest!);
    return g;
  }

  private noiseBurst(filter: BiquadFilterType, freq: number, q: number, gain: number, decay: number, at = 0) {
    const ctx = this.synth;
    const t = ctx.currentTime + at;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 0.8 + Math.random() * 0.4;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = freq;
    f.Q.value = q;
    src.connect(f).connect(this.env(gain, 0.003, decay, t));
    src.start(t, Math.random() * 0.5, decay + 0.05);
    return f;
  }

  private tone(
    freq: number,
    gain: number,
    decay: number,
    type: OscillatorType = 'sine',
    at = 0,
    sweepTo?: number,
  ) {
    const ctx = this.synth;
    const t = ctx.currentTime + at;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (sweepTo) o.frequency.exponentialRampToValueAtTime(sweepTo, t + decay);
    o.connect(this.env(gain, 0.002, decay, t));
    o.start(t);
    o.stop(t + decay + 0.05);
  }

  /** Route the next sounds through a fresh weapon bus (so a weapon switch can cut them off). */
  private withWeaponBus(fn: () => void) {
    this.cutWeaponSounds();
    const bus = this.ctx!.createGain();
    bus.connect(this.master!);
    this.weaponBus = bus;
    this.dest = bus;
    try {
      fn();
    } finally {
      this.dest = this.master;
    }
  }

  private cutWeaponSounds() {
    if (!this.weaponBus) return;
    this.weaponBus.disconnect();
    this.weaponBus = null;
  }

  /** A footstep on a hard floor; `loudness` 0..1.5. */
  step(loudness = 1) {
    if (!this.ready) return;
    this.stepSide ^= 1;
    const ready = this.steps[this.stepSide];
    if (ready.length) this.playBuffer(ready[Math.floor(Math.random() * ready.length)], loudness);
    else this.stepSynth(this.stepSide ? 1 : 0.88, loudness);
  }

  private stepSynth(pitch: number, loudness: number) {
    this.noiseBurst('bandpass', (380 + Math.random() * 120) * pitch, 1.1, 0.32 * loudness, 0.09);
    this.noiseBurst('highpass', 2600, 0.7, 0.05 * loudness, 0.04);
    this.tone(95 * pitch, 0.25 * loudness, 0.07, 'sine', 0, 55);
  }

  jump() {
    this.step(0.8);
  }

  /** Landing; `speed` is the downward speed in units/s. */
  land(speed: number) {
    if (!this.ready || speed < 120) return;
    const k = Math.min(1.5, speed / 450);
    this.step(0.7 + k * 0.5);
    if (this.thump) this.playBuffer(this.thump, k);
    else this.tone(70, 0.35 * k, 0.12, 'sine', 0, 40);
  }

  swing(stab: boolean) {
    if (!this.ready) return;
    if (this.play('knifeSlash', stab ? 0.8 : 0.7)) return;
    const ctx = this.ctx!;
    const dur = stab ? 0.14 : 0.2;
    const f = this.noiseBurst('bandpass', stab ? 700 : 900, 2.2, stab ? 0.22 : 0.18, dur);
    f.frequency.exponentialRampToValueAtTime(stab ? 1800 : 3200, ctx.currentTime + dur);
  }

  /** The knife hit something (that didn't break). */
  knifeHit(surface: Surface) {
    if (!this.ready) return;
    const played =
      surface === 'metal'
        ? this.play('knifeHitMetal', 0.9)
        : surface === 'glass'
          ? this.play('impactGlass', 0.6)
          : this.play('knifeHitWall', 0.9);
    if (played) return;
    this.noiseBurst('highpass', 1800, 0.8, 0.18, 0.05);
    this.tone(1850, 0.08, 0.12, 'triangle');
    this.tone(2790, 0.05, 0.09, 'triangle');
  }

  /** Kept for callers that don't know the surface. */
  hitWall() {
    this.knifeHit('concrete');
  }

  /** Pistol shot. Synthesised: a muffled thump with hiss when silenced, a sharp crack with a room tail otherwise. */
  shot(silenced: boolean) {
    if (!this.ready) return;
    if (this.play(silenced ? 'uspSilenced' : 'uspShot', silenced ? 0.8 : 0.75)) return;
    if (silenced) {
      this.noiseBurst('bandpass', 1400, 0.9, 0.3, 0.09);
      this.noiseBurst('lowpass', 500, 0.7, 0.35, 0.06);
      this.tone(180, 0.2, 0.05, 'sine', 0, 70);
      this.tone(2400, 0.03, 0.03, 'square', 0.005);
      this.tone(1600, 0.04, 0.03, 'square', 0.06);
      return;
    }
    // Dry crack and body, plus the same through the room reverb.
    this.noiseBurst('highpass', 2500, 0.6, 0.55, 0.05);
    this.noiseBurst('lowpass', 3200, 0.5, 0.9, 0.16);
    this.tone(120, 0.7, 0.14, 'sine', 0, 40);
    this.tone(60, 0.4, 0.2, 'sine', 0.01, 35);
    if (this.reverb) {
      const keep = this.dest;
      this.dest = this.reverb;
      this.noiseBurst('lowpass', 2400, 0.5, 0.6, 0.12);
      this.dest = keep;
    }
    this.tone(1900, 0.03, 0.03, 'square', 0.07);
  }

  /** A bullet hitting a surface at `pos`. */
  impact(surface: Surface, pos: SoundPos) {
    if (!this.ready) return;
    const id: SampleId =
      surface === 'metal'
        ? 'impactMetal'
        : surface === 'wood'
          ? 'impactWood'
          : surface === 'glass'
            ? 'impactGlass'
            : 'impactConcrete';
    const played = this.play(id, 0.6, 0, pos);
    if ((surface === 'metal' || surface === 'concrete') && Math.random() < 0.25)
      this.play('ricochet', 0.35, 0.02, pos);
    if (played) return;
    const k = falloff(pos.dist);
    if (k < 0.02) return;
    if (surface === 'glass') {
      for (let i = 0; i < 3; i++)
        this.tone(3000 + Math.random() * 3000, 0.05 * k, 0.08, 'sine', Math.random() * 0.05);
    } else if (surface === 'metal') {
      this.tone(1300 + Math.random() * 600, 0.08 * k, 0.15, 'triangle');
    }
    this.noiseBurst('bandpass', surface === 'wood' ? 900 : 1600, 1, 0.25 * k, 0.06);
    this.tone(surface === 'wood' ? 220 : 160, 0.12 * k, 0.05, 'sine', 0, 80);
  }

  /** Magazine out, magazine in, slide release, spread over the reload time. */
  reload(seconds: number) {
    if (!this.ready) return;
    this.withWeaponBus(() => {
      const click = (at: number, f: number, g: number) => {
        this.tone(f, g, 0.04, 'square', at);
        this.noiseBurst('highpass', 3000, 0.8, g * 0.8, 0.03, at);
      };
      if (!this.play('uspClipOut', 0.8, seconds * 0.2)) click(seconds * 0.2, 1500, 0.07);
      if (!this.play('uspClipIn', 0.8, seconds * 0.55)) {
        click(seconds * 0.55, 1100, 0.1);
        click(seconds * 0.62, 1300, 0.08);
      }
      if (!this.play('uspSlideRelease', 0.8, seconds * 0.8)) {
        click(seconds * 0.8, 900, 0.12);
        click(seconds * 0.83, 1700, 0.08);
      }
    });
  }

  /** Screwing the silencer on or off. */
  silencer(attach: boolean, seconds: number) {
    if (!this.ready) return;
    this.withWeaponBus(() => {
      if (this.play(attach ? 'uspSilencerOn' : 'uspSilencerOff', 0.8, seconds * 0.35)) return;
      for (let i = 0; i < 4; i++)
        this.noiseBurst('bandpass', 2200 + i * 150, 4, 0.06, 0.08, seconds * (0.2 + i * 0.08));
      this.tone(1300, 0.06, 0.04, 'square', seconds * 0.55);
    });
  }

  /** Drawing a weapon (cuts off a reload in progress). */
  deploy(pistol: boolean) {
    if (!this.ready) return;
    this.withWeaponBus(() => {
      if (pistol) {
        if (this.play('uspSlideBack', 0.8, 0.12)) return;
        this.tone(1200, 0.06, 0.03, 'square', 0.05);
        this.tone(800, 0.08, 0.04, 'square', 0.18);
      } else if (!this.play('knifeDeploy', 0.8)) this.noiseBurst('bandpass', 2500, 3, 0.08, 0.15);
    });
  }

  glass() {
    if (!this.ready) return;
    if (this.play('glassBreak', 0.8)) return;
    this.noiseBurst('highpass', 2400, 0.6, 0.35, 0.28);
    for (let i = 0; i < 7; i++)
      this.tone(2800 + Math.random() * 3400, 0.05, 0.08 + Math.random() * 0.15, 'sine', Math.random() * 0.25);
  }
}

/** Loudness by distance: full up close, fading out by ~3000 units. */
function falloff(dist: number): number {
  return Math.max(0, Math.min(1, 1 - (dist - 200) / 2800));
}
